import { useState } from 'react';
import { Send } from 'lucide-react';

import { REPLY_MAX_LENGTH, type ThreadComment } from '@/api/comments';
import { pickCommentDisplay } from '@/utils/commentDisplay';
import { useTranslation } from '@/utils/i18n';
import { cn } from '@/lib/utils';

/**
 * reco-comment-replies v2 — the conversation half of the reactions sheet.
 *
 * It lives INSIDE the sheet rather than behind a route: the app already knew
 * how to land on a given card (`uiSignals.landOnRecoId`, and `openSongCard`
 * which chains circle → card → home), so a dedicated route added an address
 * the app did not need — and cost the user their circle and their scroll
 * position every time they closed it.
 *
 * Flat, never nested: a circle is five people, and threading would double the
 * display and the notification logic for nothing.
 */
export default function ThreadPanel({
  thread,
  canReply,
  onSend,
  label,
}: {
  thread: ThreadComment[];
  /** False on a public reco: the server refuses a reply there (403). */
  canReply: boolean;
  onSend?: (content: string) => Promise<void>;
  label: (key: string) => string;
}) {
  const { language } = useTranslation();
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [showOriginal, setShowOriginal] = useState<Record<number, boolean>>({});

  const remaining = REPLY_MAX_LENGTH - draft.length;

  const send = async () => {
    const text = draft.trim();
    if (!text || sending || !onSend) return;
    setSending(true);
    try {
      await onSend(text);
      setDraft('');
    } catch {
      // Keep the draft. Losing what someone typed because the network blinked
      // is the least forgivable failure a composer can have.
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="thread-panel">
      <ul className="tw:flex tw:max-h-[40vh] tw:flex-col tw:gap-3 tw:overflow-y-auto">
        {thread.length === 0 ? (
          <p
            className="tw:py-6 tw:text-center tw:text-[13px] tw:text-grey-3"
            data-testid="thread-empty"
          >
            {label('thread.empty')}
          </p>
        ) : (
          thread.map((c) => {
            const display = pickCommentDisplay(c, language);
            const original = showOriginal[c.id] === true;
            return (
              <li key={c.id} className="tw:flex tw:gap-2" data-testid={`thread-message-${c.id}`}>
                <span className="tw:flex tw:size-8 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:bg-blue-primary tw:text-[11px] tw:font-semibold tw:text-white">
                  {(c.author?.username ?? '?').slice(0, 2).toUpperCase()}
                </span>
                <div className="tw:min-w-0">
                  <p className="tw:text-[11px] tw:text-grey-3">{c.author?.username}</p>
                  <p className="tw:text-sm tw:text-white">{original ? c.content : display.text}</p>
                  {/* Per message, and SELF-ERASING: `isTranslated` is false when
                      the translation equals the original, so a monolingual
                      circle shows no control at all. */}
                  {display.isTranslated && (
                    <button
                      type="button"
                      data-testid={`thread-original-${c.id}`}
                      onClick={() => setShowOriginal((s) => ({ ...s, [c.id]: !original }))}
                      className="tw:mt-0.5 tw:text-[11px] tw:text-grey-4 tw:underline"
                    >
                      {label(original ? 'thread.seeTranslation' : 'thread.seeOriginal')}
                    </button>
                  )}
                </div>
              </li>
            );
          })
        )}
      </ul>

      {canReply && (
        <div className="tw:mt-3 tw:flex tw:items-end tw:gap-2" data-testid="thread-composer">
          <div className="tw:flex tw:flex-1 tw:flex-col">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value.slice(0, REPLY_MAX_LENGTH))}
              placeholder={label('thread.placeholder')}
              rows={1}
              data-testid="thread-input"
              className="tw:w-full tw:resize-none tw:rounded-2xl tw:bg-dark-primary tw:px-3 tw:py-2 tw:text-sm tw:text-white tw:outline-none"
            />
            {/* Shown from the first keystroke: the limit must be visible before
                it is hit, never discovered when sending fails. */}
            <span
              data-testid="thread-counter"
              className={cn(
                'tw:self-end tw:pr-1 tw:pt-1 tw:text-[11px]',
                remaining <= 20 ? 'tw:text-yellow-primary' : 'tw:text-grey-4',
              )}
            >
              {remaining}
            </span>
          </div>
          <button
            type="button"
            onClick={send}
            disabled={sending || draft.trim().length === 0}
            data-testid="thread-send"
            className="tw:mb-6 tw:flex tw:size-9 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:bg-blue-primary tw:text-white tw:disabled:opacity-40"
          >
            <Send size={16} />
          </button>
        </div>
      )}
    </div>
  );
}
